import {
    DrawingObject,
    DrawingObjectCategory,
    DrawingObjectCenterStyle,
    DrawingObjectCenterUnits,
    DrawingObjectCircleGeometry,
    DrawingObjectFillStyle,
    DrawingObjectGeometry,
    DrawingObjectLabelAlignment,
    DrawingObjectLineType,
    DrawingObjectOffsetUnits,
    DrawingObjectPolygonGeometry,
    DrawingObjectRectangleGeometry,
    DrawingObjectType
} from '../drawing-object/types';
import * as turf from '@turf/turf';
import dayjs from 'dayjs';
import { FPMStoreAreaMessage, GEO_COORD } from '../../messages/message-parser';
import { FPMSyncType } from '../../common/enums';
import { julianOffsetToDate } from '../../common/helpers';

export type ExtractKeysExtend<T, KK> = {
    [K in keyof T]: T[K] extends KK ? K : never;
}[keyof T];

import { Map as MaplibreMap } from 'maplibre-gl';

export function setCursor(map: MaplibreMap, cursor: string | null) {
    map._canvas.style.cursor = cursor ?? 'inherit';
}

export function drawingObjectToGeoJson(type: DrawingObjectType, geometry: DrawingObjectGeometry): GeoJSON.Feature<GeoJSON.Polygon> {
    if (type === DrawingObjectType.circle) {
        const g = geometry as DrawingObjectCircleGeometry;
        if (g.radiusX === 0 || g.radiusY === 0) {
            return turf.polygon([]);
        }
        const units = g.radiusUnits === DrawingObjectOffsetUnits.degrees ? 'degrees' : 'nauticalmiles';
        return turf.ellipse([g.centerX, g.centerY], g.radiusX, g.radiusY, { units });
    }

    if (type === DrawingObjectType.rectangle) {
        const g = geometry as DrawingObjectRectangleGeometry;
        // Convert width and height from kilometers to degrees
        const halfWidth = g.width / 2;
        const halfHeight = g.height / 2;

        const center = [g.centerX, g.centerY];
        if (g.sizeUnits === DrawingObjectOffsetUnits.degrees) {
            return turf.bboxPolygon([center[0] - halfWidth, center[1] - halfHeight, center[0] + halfWidth, center[1] + halfHeight]);
        }
        // Create bounding box by calculating corner coordinates
        const e = turf.rhumbDestination(center, halfWidth, 90, { units: 'nauticalmiles' }).geometry.coordinates;
        const w = turf.rhumbDestination(center, halfWidth, -90, { units: 'nauticalmiles' }).geometry.coordinates;
        const n = turf.rhumbDestination(center, halfHeight, 0, { units: 'nauticalmiles' }).geometry.coordinates;
        const s = turf.rhumbDestination(center, halfHeight, 180, { units: 'nauticalmiles' }).geometry.coordinates;
        return turf.bboxPolygon([w[0], s[1], e[0], n[1]]);
    }

    const g = geometry as DrawingObjectPolygonGeometry;
    return turf.polygon([g.polygonCoordinates]);
}

export function isDrawingObjectActive(object: DrawingObject, originalCurrentDate: Date): boolean {
    const currentDate = dayjs(originalCurrentDate).utc();
    if (!object.validTime) {
        return true;
    }

    //check days
    const currentDay = dayjs(currentDate).day();
    const isDayActive = (object.validTime.activeDaysOfWeek & (1 << currentDay)) !== 0;

    if (!isDayActive) {
        return false;
    }

    //check datetimes
    const startDate = dayjs(object.validTime.startDay);
    const endDate = dayjs(object.validTime.endDay);

    if (startDate.isValid()) {
        if (currentDate < startDate) {
            return false;
        }
    }
    if (endDate.isValid()) {
        if (currentDate > endDate) {
            return false;
        }
    }

    //check time of day
    let startTime = dayjs('invalid');
    if (object.validTime?.startTime) {
        const [hours, minutes, seconds] = object.validTime?.startTime.split(':').map(Number) as number[];
        startTime = dayjs(currentDate).utc().hour(hours).minute(minutes).second(seconds);
    }
    let endTime = dayjs('invalid');
    if (object.validTime?.endTime) {
        const [hours, minutes, seconds] = object.validTime?.endTime.split(':').map(Number) as number[];
        endTime = dayjs(currentDate).utc().hour(hours).minute(minutes).second(seconds);
    }

    if (startTime.isValid() && currentDate.isBefore(startTime)) {
        return false;
    }
    if (endTime.isValid() && currentDate.isAfter(endTime)) {
        return false;
    }

    return true;
}

function formatTimeFromHourMinute(hour: number | undefined, minute: number | undefined): string | null {
    if (hour === undefined || minute === undefined) {
        return null;
    }
    return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}:00`;
}

function isSameTimeIgnoringSeconds(time1: string | null, time2: string | null): boolean {
    if (!time1 || !time2) {
        return false;
    }
    const [h1, m1] = time1.split(':').map(Number);
    const [h2, m2] = time2.split(':').map(Number);
    return h1 === h2 && m1 === m2;
}

function isSameDateIgnoringTime(date1: string | null, date2: Date | null): boolean {
    if (!date1 || !date2) {
        return false;
    }
    return dayjs(date1).format('YYYY-MM-DD') === dayjs(date2).format('YYYY-MM-DD');
}

function createDateFromJulianOffset(julianOffset: number | undefined): Date | null {
    if (julianOffset === undefined) {
        return null;
    }
    return julianOffsetToDate(0, 0, julianOffset);
}

export function storeAreaToDrawingObject(message: FPMStoreAreaMessage, existingObject: DrawingObject | null): DrawingObject {
    const {
        id = '',
        isCompany = false,
        type = !existingObject ? drawingObjectTypeFromPolygon(message.psPoints) : DrawingObjectType.polygon,
        label = {},
        line = {},
        fill = {},
        center = {},
        zoom = {},
        altitude = {},
        alerts = {},
        category = DrawingObjectCategory.avoidance,
        fpmProperties = {},
        validTime = {}
    } = existingObject || {};

    const messageStartTime = formatTimeFromHourMinute(message.cStartHour, message.cStartMinute);
    const useExistingStartTime =
        existingObject?.validTime?.startTime && isSameTimeIgnoringSeconds(existingObject.validTime.startTime, messageStartTime);
    const startTime = useExistingStartTime ? existingObject.validTime.startTime : messageStartTime;

    const messageEndTime = formatTimeFromHourMinute(message.cEndHour, message.cEndMinute);
    const useExistingEndTime =
        existingObject?.validTime?.endTime && isSameTimeIgnoringSeconds(existingObject.validTime.endTime, messageEndTime);
    const endTime = useExistingEndTime ? existingObject.validTime.endTime : messageEndTime;

    const messageStartDay = createDateFromJulianOffset(message.dwStartJDay);
    const useExistingStartDay =
        existingObject?.validTime?.startDay && isSameDateIgnoringTime(existingObject.validTime.startDay, messageStartDay);
    const startDay = useExistingStartDay
        ? existingObject.validTime.startDay
            ? new Date(existingObject.validTime.startDay)
            : null
        : messageStartDay;

    const messageEndDay = createDateFromJulianOffset(message.dwEndJDay);
    const useExistingEndDay = existingObject?.validTime?.endDay && isSameDateIgnoringTime(existingObject.validTime.endDay, messageEndDay);
    const endDay = useExistingEndDay ? (existingObject.validTime.endDay ? new Date(existingObject.validTime.endDay) : null) : messageEndDay;

    const object: Omit<DrawingObject, 'geometry'> = {
        id,
        name: message.pczObjID,
        isCompany,
        type,
        label: {
            alignment: DrawingObjectLabelAlignment.perimeter,
            color: '',
            ...label,
            text: message.pczLabelID
        },
        line: {
            style: DrawingObjectLineType.solid,
            width: 1,
            color: '#fff',
            ...line
        },
        fill: {
            style: DrawingObjectFillStyle.solid,
            color: '#fff',
            ...fill
        },
        center: {
            style: DrawingObjectCenterStyle.plus,
            color: '',
            size: 0,
            ...center
        },
        zoom: {
            object: 0,
            point: 0,
            label: 0,
            ...zoom
        },
        altitude: {
            ...altitude,
            min: message.wLower ?? existingObject?.altitude?.min ?? null,
            max: message.wUpper ?? existingObject?.altitude?.max ?? null
        },
        alerts: {
            plane: false,
            route: false,
            ...alerts
        },
        category,
        fpmProperties: {
            sync: true,
            ...fpmProperties,
            FPMSyncType: message.cUserOnly ? FPMSyncType.userSpecific : FPMSyncType.global
        },
        validTime: {
            greyOutNonActive: false,
            ...validTime,
            startTime,
            endTime,
            startDay: startDay?.toISOString() ?? null,
            endDay: endDay?.toISOString() ?? null,
            activeDaysOfWeek: message.cDaysOfWeekActive ?? 0
        }
    };

    (object as DrawingObject).geometry = !existingObject ? createGeometryFromPoints(message.psPoints, type) : existingObject.geometry;
    return object as DrawingObject;
}

function createGeometryFromPoints(points: GEO_COORD[], type: DrawingObjectType): DrawingObjectGeometry {
    const coords = points.map((p) => [p.iLongitude, p.iLatitude]);

    if (type === DrawingObjectType.circle) {
        const center = turf.centroid(turf.polygon([coords])).geometry.coordinates;

        // Calculate principal axes of the ellipse
        const centeredCoords = coords.map((coord) => [coord[0] - center[0], coord[1] - center[1]]);

        // Calculate covariance matrix elements
        const n = centeredCoords.length;
        const xx = centeredCoords.reduce((sum, coord) => sum + coord[0] * coord[0], 0) / n;
        const yy = centeredCoords.reduce((sum, coord) => sum + coord[1] * coord[1], 0) / n;
        const xy = centeredCoords.reduce((sum, coord) => sum + coord[0] * coord[1], 0) / n;

        // Calculate eigenvalues to get principal axes
        const trace = xx + yy;
        const det = xx * yy - xy * xy;
        const discriminant = Math.sqrt(trace * trace - 4 * det);

        const eigenvalue1 = (trace + discriminant) / 2;
        const eigenvalue2 = (trace - discriminant) / 2;

        const radiusX = Math.sqrt(eigenvalue1) * 2; // Semi-major axis
        const radiusY = Math.sqrt(eigenvalue2) * 2; // Semi-minor axis

        return {
            centerX: center[0],
            centerY: center[1],
            centerUnits: DrawingObjectCenterUnits.latLng,
            radiusX: turf.distance([0, 0], [radiusX, 0], { units: 'nauticalmiles' }),
            radiusY: turf.distance([0, 0], [0, radiusY], { units: 'nauticalmiles' }),
            radiusUnits: DrawingObjectOffsetUnits.miles
        };
    }

    if (type === DrawingObjectType.rectangle) {
        const bbox = turf.bbox(turf.polygon([coords]));
        const center = [(bbox[0] + bbox[2]) / 2, (bbox[1] + bbox[3]) / 2];
        const width = turf.distance([bbox[0], center[1]], [bbox[2], center[1]], { units: 'nauticalmiles' });
        const height = turf.distance([center[0], bbox[1]], [center[0], bbox[3]], { units: 'nauticalmiles' });

        return {
            centerX: center[0],
            centerY: center[1],
            centerUnits: DrawingObjectCenterUnits.latLng,
            width: width,
            height: height,
            sizeUnits: DrawingObjectOffsetUnits.miles
        };
    }

    return {
        polygonCoordinates: coords
    };
}

function drawingObjectTypeFromPolygon(points: GEO_COORD[]): DrawingObjectType {
    // Check if it's a rectangle by testing exact bounding box pattern
    if (points.length === 5 && points[0].iLatitude === points[4].iLatitude && points[0].iLongitude === points[4].iLongitude) {
        const coords = points.slice(0, 4);

        // Get unique latitudes and longitudes
        const lats = [...new Set(coords.map((p) => p.iLatitude))].sort((a, b) => a - b);
        const lngs = [...new Set(coords.map((p) => p.iLongitude))].sort((a, b) => a - b);

        // Rectangle must have exactly 2 unique lats and 2 unique lngs
        if (lats.length === 2 && lngs.length === 2) {
            // Check if all 4 corner combinations exist
            const corners = [
                [lngs[0], lats[0]],
                [lngs[1], lats[0]],
                [lngs[1], lats[1]],
                [lngs[0], lats[1]]
            ];

            const actualPoints = coords.map((p) => [p.iLongitude, p.iLatitude]);
            const tolerance = 0.1;

            const isRectangle = corners.every((corner) =>
                actualPoints.some((point) => Math.abs(point[0] - corner[0]) < tolerance && Math.abs(point[1] - corner[1]) < tolerance)
            );

            if (isRectangle) {
                return DrawingObjectType.rectangle;
            }
        }
    }

    // circle/ellipse detection
    if (points.length >= 32) {
        const coords = points.map((p) => [p.iLongitude, p.iLatitude]);
        const center = turf.centroid(turf.polygon([coords])).geometry.coordinates;

        // Calculate distances from center
        const distances = coords.map((coord) => turf.distance(center, coord, { units: 'meters' }));
        const maxDist = Math.max(...distances);
        const minDist = Math.min(...distances);
        const avgDist = distances.reduce((sum, d) => sum + d, 0) / distances.length;

        // Check if distances follow elliptical pattern (consistent variation)
        const variance = distances.reduce((sum, d) => sum + Math.pow(d - avgDist, 2), 0) / distances.length;
        const stdDev = Math.sqrt(variance);
        const coefficientOfVariation = stdDev / avgDist;

        const isElliptical = coefficientOfVariation < 0.15 && minDist / maxDist > 0.3;

        if (isElliptical) {
            return DrawingObjectType.circle;
        }
    }

    return DrawingObjectType.polygon;
}

export function getEnumValues<T extends number>(enumObj: Record<string, T | string>): T[] {
    return Object.values(enumObj).filter((v): v is T => typeof v === 'number');
}
